import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, File as FileIcon, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";

const ACCEPT = ".stl,.obj,.ply,.jpg,.jpeg,.png,.webp,.pdf,.dcm,.zip,.doc,.docx,.xls,.xlsx,.csv,.txt";

export default function NewCase() {
  const [form, setForm] = useState({
    patient_ref: "", arch: "", case_type: "", software_preference: "",
    treatment_goals: "", clinical_notes: "", urgency: "standard",
  });
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState("idle"); // idle | creating | uploading | done
  const [uploadProgress, setUploadProgress] = useState("");
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const addFiles = (list) => {
    const next = [...files];
    for (const f of list) next.push(f);
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.arch || !form.case_type || !form.software_preference) {
      toast.error("Please complete all required fields.");
      return;
    }
    setStage("creating");
    try {
      const { data: created } = await api.post("/cases", form);
      if (files.length) {
        setStage("uploading");
        for (let i = 0; i < files.length; i++) {
          setUploadProgress(`Uploading ${i + 1} of ${files.length}: ${files[i].name}`);
          const fd = new FormData();
          fd.append("file", files[i]);
          fd.append("kind", "intake");
          await api.post(`/cases/${created.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      setStage("done");
      toast.success(`Case ${created.case_id} submitted successfully.`);
      navigate(`/portal/cases/${created.id}`);
    } catch (err) {
      setStage("idle");
      toast.error(formatApiError(err));
    }
  };

  const busy = stage !== "idle";

  return (
    <div className="max-w-3xl" data-testid="new-case-page">
      <h1 className="font-display font-black text-3xl tracking-tight">Submit a Case</h1>
      <p className="text-sm text-slate-500 mt-1 mb-8">Clinical objectives first — tell us the outcome you want, then attach the records.</p>

      <form onSubmit={submit} className="space-y-6" data-testid="new-case-form">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">Case details</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="nc-ref">Patient / case reference *</Label>
              <Input id="nc-ref" data-testid="case-patient-ref-input" required value={form.patient_ref} onChange={set("patient_ref")}
                placeholder="e.g. JS-1042 (initials + chart no.)" className="h-11" />
              <p className="text-[11px] text-slate-400">Use a de-identified reference, not the full patient name.</p>
            </div>
            <div className="space-y-2">
              <Label>Arch *</Label>
              <Select value={form.arch} onValueChange={set("arch")}>
                <SelectTrigger data-testid="case-arch-select" className="h-11"><SelectValue placeholder="Select arch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Upper">Upper only</SelectItem>
                  <SelectItem value="Lower">Lower only</SelectItem>
                  <SelectItem value="Both">Both arches</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Case type *</Label>
              <Select value={form.case_type} onValueChange={set("case_type")}>
                <SelectTrigger data-testid="case-type-select" className="h-11"><SelectValue placeholder="Select case type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full treatment plan">Full treatment plan</SelectItem>
                  <SelectItem value="Refinement">Refinement</SelectItem>
                  <SelectItem value="Mid-course correction">Mid-course correction</SelectItem>
                  <SelectItem value="Setup review / second opinion">Setup review / second opinion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Software / platform preference *</Label>
              <Select value={form.software_preference} onValueChange={set("software_preference")}>
                <SelectTrigger data-testid="case-software-select" className="h-11"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  {["Invisalign ClinCheck", "ClearCorrect", "3Shape", "uLab", "Blue Sky Plan", "OrthoAnalyzer", "No preference"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Urgency</Label>
              <Select value={form.urgency} onValueChange={set("urgency")}>
                <SelectTrigger data-testid="case-urgency-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (72h)</SelectItem>
                  <SelectItem value="priority">Priority (48h)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nc-goals">Treatment goals *</Label>
            <Textarea id="nc-goals" data-testid="case-goals-input" required rows={4} value={form.treatment_goals} onChange={set("treatment_goals")}
              placeholder="Diagnosis, objectives, clinical priorities — e.g. relieve lower anterior crowding with IPR, maintain Class I, avoid expansion…" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nc-notes">Clinical notes & preferences</Label>
            <Textarea id="nc-notes" data-testid="case-notes-input" rows={3} value={form.clinical_notes} onChange={set("clinical_notes")}
              placeholder="Attachment preferences, staging velocity, elastics, limitations, anything the planner should know…" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Case files</h2>
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="case-upload-dropzone"
            className="w-full border-2 border-dashed border-slate-200 hover:border-sky-400 rounded-xl py-10 flex flex-col items-center gap-3 transition-colors group">
            <UploadCloud className="h-8 w-8 text-slate-300 group-hover:text-sky-500 transition-colors" />
            <p className="text-sm font-semibold text-slate-600">Click to upload scans, photos & X-rays</p>
            <p className="text-xs text-slate-400">STL, OBJ, PLY, JPG, PNG, PDF, DICOM, ZIP — up to 100MB each</p>
          </button>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="case-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          {files.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="case-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <FileIcon className="h-4 w-4 text-sky-600 shrink-0" />
                    <span className="truncate font-medium">{f.name}</span>
                    <span className="text-xs text-slate-400 shrink-0">{(f.size / 1048576).toFixed(1)} MB</span>
                  </span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`case-file-remove-${i}`}
                    className="text-slate-400 hover:text-red-500 transition-colors ml-3"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between bg-sky-50 border border-sky-100 rounded-xl px-5 py-4">
          <p className="text-xs text-sky-800 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Files are access-controlled — only your account and the planning team can view them.
          </p>
        </div>

        <Button type="submit" disabled={busy} data-testid="case-submit-btn"
          className="w-full sm:w-auto rounded-full bg-sky-600 hover:bg-sky-700 text-white font-bold px-10 h-12 text-sm">
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {stage === "creating" ? "Creating case…" : uploadProgress}</> : "Submit case for planning"}
        </Button>
      </form>
    </div>
  );
}
